import iconRebranding from 'img/trial/rebranding.svg?url';
import icon from 'img/trial/trial.svg?url';
import { extInfo } from 'lib/extInfo';
import { PROMO_TARIFFS, USER_EMAIL } from 'reactApp/appHelpers/configHelpers';
import { isRebranding } from 'reactApp/appHelpers/featuresHelpers';
import { offerBannerParams } from 'reactApp/appHelpers/featuresHelpers/features/offerBanner';
import { getExtension, getItemNameWithoutExt, getNameById, getParent, isFolder, normalizePublicApiInfo } from 'reactApp/modules/file/utils';
import type {
    ApiFolderResponse,
    ApiFolderResponseFile,
    IFolderFlags,
    Item,
    NormalizeFileFunc,
    NormalizeFolderFunc,
} from 'reactApp/modules/home/home.types';
import type { ISpacePromoConfig } from 'reactApp/modules/promo/promo.types';
import { ESortOder, ESortTypes } from 'reactApp/modules/sort/sort.types';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UrlBuilder } from 'reactApp/modules/urlBuilder/UrlBuilder';
import type { AccessRights } from 'reactApp/types/Tree';
import { normalizeMtime, renameCameraUploadFolder } from 'reactApp/utils/tree.helpers';

export const isApiFolderType = (item: ApiFolderResponse | ApiFolderResponseFile): item is ApiFolderResponse => isFolder(item);

const urlBuilder = new UrlBuilder();

const injectIntraDomainShareProp = (localIntraDomainShare?: boolean, globalIntraDomainShare?: boolean) => {
    const hasSomeIntraDomainShareDefined = !!localIntraDomainShare || !!globalIntraDomainShare;
    return hasSomeIntraDomainShareDefined && { intraDomainShare: true };
};

export const normalizeItem = (item: ApiFolderResponse | ApiFolderResponseFile, parent?: string, globalIntraDomainShare?: boolean): Item => {
    const id = item.home;
    const parentId = parent || getParent(id);
    const mode_bitmask = item.mode_bitmask;

    return {
        id,
        parent: id === '/' ? '' : parentId,
        type: item.type,
        name: item.name || getNameById(id),
        home: id,
        size: item.size,
        descriptorId: item.descriptorId,
        mode_bitmask,
        __reduxTree: true,
        ...injectIntraDomainShareProp(item.intra_domain_share, globalIntraDomainShare),
        ...normalizePublicApiInfo(item),
    };
};

export const normalizeFile: NormalizeFileFunc = ({ item, normalizedItem }) => {
    const ext = getExtension(item);
    const nameWithoutExt = getItemNameWithoutExt(item.name, ext);
    const author = item.attr ? item.attr.actor : USER_EMAIL;
    const { kind, subKind } = extInfo.get(ext);
    const path = item.home;

    return {
        ...normalizedItem,
        mtime: normalizeMtime(item.mtime),
        virusScan: item.virus_scan, // temp
        hash: item.hash,
        ext,
        attr: item.attr,
        nameWithoutExt,
        author,
        storage: EStorageType.home,
        kind,
        subKind,
        isMounted: Boolean(item.flags?.mounted),
        isFolder: false,
        uflr: item.uflr,
        isInFavorites: !!item.favorite,
        thumbnails: urlBuilder.getThumb({
            hash: item.hash,
            ext,
            id: normalizedItem.id,
            size: normalizedItem.size,
            kind,
            name: item.name,
            path,
            isPublic: false,
            isStock: false,
            dwl_token: null,
            weblink: '',
        }),
        url: urlBuilder.getUrls({
            ext,
            isPublic: false,
            id: normalizedItem.id,
            weblink: '',
            size: normalizedItem.size,
            kind,
            subKind,
            isStock: false,
            name: item.name,
            path,
        }),
    };
};

export const normalizeFlags = (flags, rewrite?, oldFlags?): IFolderFlags | undefined => {
    if (!flags) {
        return;
    }
    // чтобы обычный паблик не переключался в состояние альбома,
    // но альбом может переключиться в состояние обычного просмотра
    if (flags.is_gallery && (rewrite || oldFlags?.isMedia)) {
        return {
            isMedia: true,
        };
    }
};

export const normalizeFolder: NormalizeFolderFunc = ({ item, normalizedItem, isLoaded }) => {
    const folders = item.count?.folders ?? 0;
    const files = item.count?.files ?? 0;
    const count = {
        folders,
        files,
        all: folders + files,
        loaded: (item.list || []).length,
    };

    return {
        ...normalizedItem,
        name: normalizedItem.id === '/' ? 'Все файлы' : renameCameraUploadFolder(item),
        isReadOnly: !!item.readonly,
        storage: EStorageType.home,
        isFolder: true,
        __reduxTree: true,
        rev: item.rev,
        kind: item.kind,
        isLoading: false,
        tree: item.tree,
        isInFavorites: !!item.favorite,
        count,
        quota: item.quota,
        sort: item.sort || { type: ESortTypes.name, order: ESortOder.asc },
        hasMoreToLoad: Boolean(isLoaded && count.loaded < count.all),
        childs: [],
        isLoaded,
        isMounted: item.kind === 'mounted' || Boolean(item?.flags?.mounted),
        flags: normalizeFlags(item.flags, true),
    };
};

export const getTrialOfferConfig = (): ISpacePromoConfig => {
    let { title, text, button, features, tariffId } = offerBannerParams;

    if (!title) {
        title = isRebranding ? '128 ГБ на месяц за 1 ₽' : '128 ГБ на месяц за 1 рубль';
    }

    if (!text) {
        text = 'Свободное место для десятков тысяч фото и документов';
    }

    if (!button) {
        button = isRebranding ? 'Попробовать' : 'Попробовать за 1 рубль';
    }

    if (!tariffId) {
        tariffId = PROMO_TARIFFS?.default128GbTrial;
    }

    if (!features?.length) {
        features = [
            'Без\xA0рекламы в\xA0Почте и\xA0Облаке',
            'Загрузка файлов размером до\xA0100\xA0ГБ',
            'Возможность делиться подпиской с\xA0близкими',
        ];
    }

    return {
        id: 'trialOffer',
        icon: isRebranding ? iconRebranding : icon,
        title,
        text,
        button,
        tariffId,
        features,
    };
};

interface PublicOptionsInfo {
    weblinkAccessRights?: AccessRights;
    weblinkExpires?: number;
    weblinkDomestic?: boolean;
    weblinkAutoDelete?: boolean;
    weblinkSeoEnabled?: boolean;
    weblinkEnabledDisabledDownload?: boolean;
}

export const processToggleSeo = <T extends PublicOptionsInfo>(item?: T) => {
    if (!item) {
        return;
    }

    item.weblinkSeoEnabled = !item.weblinkSeoEnabled;

    if (item.weblinkSeoEnabled) {
        item.weblinkExpires = undefined;
        item.weblinkAccessRights = undefined;
        item.weblinkDomestic = false;
        if ('weblinkAutoDelete' in item) {
            item.weblinkAutoDelete = false;
        }
    }
};

export const processToggleDisablePublicDownload = <T extends PublicOptionsInfo>(item?: T, disableDownload?: boolean) => {
    if (!item) {
        return;
    }

    item.weblinkEnabledDisabledDownload = Boolean(disableDownload);
};

export const isTheSameFile = (file: { hash?: string }, descriptor: { hash?: string }) => {
    const descriptorHash = descriptor?.hash;
    const fileHash = file?.hash;
    return fileHash === descriptorHash;
};
